import React, { useState, useEffect, useRef } from "react";
import { Button, Loader } from "@mantine/core";
import {
  IconMicrophone,
  IconMicrophoneOff,
  IconPhoneOff,
  IconVideo,
  IconVideoOff,
} from "@tabler/icons-react";
import AgoraRTC from "agora-rtc-sdk-ng";
import type { VideoCallProps } from "../../types";
import apiService from "../../services/api";

interface AgoraLocalConfig {
  appId: string;
  token: string;
  channel: string;
  uid: string;
}

interface AgoraUser {
  uid: string | number;
  videoTrack?: { play: (element: HTMLElement) => void } | null;
  audioTrack?: { play: () => void } | null;
  [key: string]: unknown;
}

const VideoCall: React.FC<VideoCallProps> = ({
  sessionId,
  trainerId,
  clientId: _clientId,
  onSessionEnd,
  onBackToClientPortal,
  sessionOptions = {},
  durationMinutes = undefined,
  petInfo = null,
  lastSessionInfo = null,
  sessionStartedAt = null,
  initialNotes = "",
}) => {
  const [_isJoined, setIsJoined] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isVideoOn, setIsVideoOn] = useState<boolean>(true);
  const [sessionTime, setSessionTime] = useState<number>(0);
  const [sessionStatus, setSessionStatus] = useState<string>("connecting");
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<AgoraUser[]>([]);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isTimeUp, setIsTimeUp] = useState<boolean>(false);
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const [sessionNotes, setSessionNotes] = useState<string>(initialNotes);
  const [notesSaveStatus, setNotesSaveStatus] = useState<"idle" | "saving" | "saved" | "error">(
    "idle",
  );
  const lastSavedNotesRef = useRef<string>(initialNotes);

  const clientRef = useRef<ReturnType<typeof AgoraRTC.createClient> | null>(null);
  const localVideoRef = useRef<HTMLDivElement | null>(null);
  const remoteVideoRef = useRef<HTMLDivElement | null>(null);
  const _remoteAudioRef = React.useRef<HTMLDivElement | null>(null);
  const localVideoTrackRef =
    useRef<
      ReturnType<typeof AgoraRTC.createCameraVideoTrack> extends Promise<infer T> ? T | null : null
    >(null);
  const localAudioTrackRef =
    useRef<
      ReturnType<typeof AgoraRTC.createMicrophoneAudioTrack> extends Promise<infer T>
        ? T | null
        : null
    >(null);
  const remoteVideoTrackRef = useRef<{ play: (element: HTMLElement) => void } | null>(null);
  const sessionTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const autoEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [agoraConfig, setAgoraConfig] = useState<AgoraLocalConfig | null>(null);
  const lastLayoutRef = useRef<"mobile" | "desktop">(
    window.innerWidth >= 768 ? "desktop" : "mobile",
  );
  const CHANNEL = `session_${sessionId}`;

  useEffect(() => {
    fetchAgoraConfig();
  }, []);

  useEffect(() => {
    if (!trainerId) return;
    if (sessionNotes === lastSavedNotesRef.current) return;

    setNotesSaveStatus("saving");
    const handle = setTimeout(async () => {
      try {
        await apiService.saveSessionNotes(sessionId, sessionNotes);
        lastSavedNotesRef.current = sessionNotes;
        setNotesSaveStatus("saved");
      } catch (err) {
        console.error("Failed to autosave session notes:", err);
        setNotesSaveStatus("error");
      }
    }, 1500);

    return () => clearTimeout(handle);
  }, [sessionNotes, trainerId, sessionId]);

  // Re-play video tracks when crossing the md (768px) breakpoint to fix dark screen
  useEffect(() => {
    const handleResize = (): void => {
      const currentLayout = window.innerWidth >= 768 ? "desktop" : "mobile";
      if (currentLayout !== lastLayoutRef.current) {
        lastLayoutRef.current = currentLayout;
        // Re-play local video into its container after layout shift
        if (localVideoTrackRef.current && localVideoRef.current) {
          localVideoTrackRef.current.play(localVideoRef.current);
        }
        // Re-play remote video into its container after layout shift
        if (remoteVideoTrackRef.current && remoteVideoRef.current) {
          remoteVideoTrackRef.current.play(remoteVideoRef.current);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (agoraConfig && agoraConfig.appId) {
      initializeAgora();
      startSessionTimer();
    }

    return () => {
      cleanup();
    };
  }, [agoraConfig]);

  const fetchAgoraConfig = async (): Promise<void> => {
    try {
      setError(null);
      setSessionStatus("initializing");

      const uid = Math.floor(Math.random() * 1000000).toString();

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8000"}/agora/token?channel=${encodeURIComponent(CHANNEL)}&uid=${uid}&sessionId=${sessionId}`,
      );

      const data = (await response.json()) as Record<string, unknown>;

      if (data.Code === 500 || !data.appId) {
        throw new Error(
          (data.Message as string) ||
            "Failed to get Agora configuration. Please ensure AGORA_APP_ID is set in the backend environment.",
        );
      }

      const token = data.token !== null && data.token !== undefined ? (data.token as string) : "";

      console.log("Agora config received:", {
        appId: data.appId,
        tokenLength: token ? token.length : 0,
        tokenPreview: token ? token.substring(0, 20) + "..." : "empty",
        channel: (data.channel as string) || CHANNEL,
        uid: uid,
      });

      setAgoraConfig({
        appId: data.appId as string,
        token: token,
        channel: (data.channel as string) || CHANNEL,
        uid: uid,
      });
    } catch (err: unknown) {
      console.error("Failed to fetch Agora config:", err);
      setError(`Failed to connect to video call: ${(err as Error).message}`);
      setSessionStatus("error");
    }
  };

  const initializeAgora = async (): Promise<void> => {
    if (!agoraConfig || !agoraConfig.appId) {
      setError("Agora App ID not available");
      setSessionStatus("error");
      return;
    }

    try {
      setError(null);
      setSessionStatus("initializing");

      clientRef.current = AgoraRTC.createClient({
        mode: "rtc",
        codec: "vp8",
      });

      clientRef.current.on("user-published", handleUserPublished);
      clientRef.current.on("user-unpublished", handleUserUnpublished);
      clientRef.current.on("user-left", handleUserLeft);
      clientRef.current.on("user-joined", handleUserJoined);

      await clientRef.current.join(
        agoraConfig.appId,
        agoraConfig.channel,
        agoraConfig.token,
        agoraConfig.uid,
      );
      setIsJoined(true);
      setSessionStatus("active");

      await publishLocalStreams();
    } catch (err: unknown) {
      console.error("Failed to initialize Agora:", err);
      setError(`Failed to connect to video call: ${(err as Error).message}`);
      setSessionStatus("error");
    }
  };

  const publishLocalStreams = async (): Promise<void> => {
    try {
      const localVideoStream = await AgoraRTC.createCameraVideoTrack({
        encoderConfig: {
          width: 1280,
          height: 720,
          frameRate: 30,
        },
      });
      localVideoTrackRef.current = localVideoStream;
      await clientRef.current!.publish(localVideoStream);

      if (localVideoRef.current) {
        localVideoStream.play(localVideoRef.current);
      }

      const localAudioStream = await AgoraRTC.createMicrophoneAudioTrack({
        encoderConfig: {
          sampleRate: 48000,
          stereo: true,
          bitrate: 48,
        },
      });
      localAudioTrackRef.current = localAudioStream;
      await clientRef.current!.publish(localAudioStream);

      console.log("Local audio track created and published");
    } catch (err: unknown) {
      console.error("Failed to publish local streams:", err);
      setError("Failed to start camera/microphone: " + (err as Error).message);
    }
  };

  const handleUserPublished = async (user: AgoraUser, mediaType: string): Promise<void> => {
    try {
      await clientRef.current!.subscribe(
        user as Record<string, unknown> as Parameters<
          typeof clientRef.current extends { subscribe: (...args: infer _A) => unknown }
            ? never
            : never
        >[0],
        mediaType as "video" | "audio",
      );

      if (mediaType === "video") {
        const remoteVideoStream = user.videoTrack;
        remoteVideoTrackRef.current = remoteVideoStream || null;
        if (remoteVideoRef.current && remoteVideoStream) {
          remoteVideoStream.play(remoteVideoRef.current);
        }
      }

      if (mediaType === "audio") {
        const remoteAudioStream = user.audioTrack;
        if (remoteAudioStream) {
          remoteAudioStream.play();
          console.log("Remote audio track playing");
        }
      }

      setParticipants((prev) => {
        const exists = prev.find((p) => p.uid === user.uid);
        if (!exists) {
          return [...prev, user];
        }
        return prev;
      });
    } catch (err) {
      console.error("Failed to subscribe to user:", err);
    }
  };

  const handleUserUnpublished = (user: AgoraUser, mediaType: string): void => {
    console.log("User unpublished:", user, mediaType);
  };

  const handleUserLeft = (user: AgoraUser): void => {
    console.log("User left:", user);
    remoteVideoTrackRef.current = null;
    setParticipants((prev) => prev.filter((p) => p.uid !== user.uid));
  };

  const handleUserJoined = (user: AgoraUser): void => {
    console.log("User joined:", user);
  };

  const toggleMute = async (): Promise<void> => {
    try {
      if (localAudioTrackRef.current) {
        const newMutedState = !isMuted;
        await localAudioTrackRef.current.setEnabled(!newMutedState);
        setIsMuted(newMutedState);
        console.log(`Audio ${newMutedState ? "muted" : "unmuted"}`);
      } else {
        console.warn("Local audio track not found");
      }
    } catch (err) {
      console.error("Failed to toggle mute:", err);
    }
  };

  const toggleVideo = async (): Promise<void> => {
    try {
      if (localVideoTrackRef.current) {
        const newVideoState = !isVideoOn;
        await localVideoTrackRef.current.setEnabled(newVideoState);
        setIsVideoOn(newVideoState);
        console.log(`Video ${newVideoState ? "turned on" : "turned off"}`);
      } else {
        console.warn("Local video track not found");
      }
    } catch (err) {
      console.error("Failed to toggle video:", err);
    }
  };

  const startSessionTimer = (): void => {
    let sessionStartTime: number;
    if (sessionStartedAt) {
      try {
        sessionStartTime = new Date(sessionStartedAt).getTime();
        console.log(
          "Using session start time from backend:",
          new Date(sessionStartTime).toISOString(),
        );
      } catch (e) {
        console.warn("Failed to parse sessionStartedAt, using current time:", e);
        sessionStartTime = Date.now();
      }
    } else {
      sessionStartTime = Date.now();
      console.log("No session start time provided, using current time");
    }

    startTimeRef.current = sessionStartTime;
    const durationSeconds = durationMinutes ? durationMinutes * 60 : null;

    sessionTimerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current!) / 1000);
      setSessionTime(elapsed);

      if (durationSeconds) {
        const remaining = Math.max(0, durationSeconds - elapsed);
        setTimeRemaining(remaining);

        if (remaining === 0 && !isTimeUp) {
          setIsTimeUp(true);
          setTimeout(() => {
            endSession();
          }, 5000);
        }
      }
    }, 1000);

    if (durationSeconds) {
      const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current!) / 1000);
      const remainingSeconds = Math.max(0, durationSeconds - elapsedSeconds);

      if (remainingSeconds > 0) {
        autoEndTimerRef.current = setTimeout(() => {
          setIsTimeUp(true);
          setTimeout(() => {
            endSession();
          }, 5000);
        }, remainingSeconds * 1000);
      }
    }
  };

  const endSession = async (): Promise<void> => {
    try {
      setSessionStatus("ending");

      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }

      if (clientRef.current) {
        await clientRef.current.leave();
      }

      cleanup();

      onSessionEnd(sessionNotes);
    } catch (err) {
      console.error("Failed to end session:", err);
      setError("Failed to end session properly");
    }
  };

  const cleanup = (): void => {
    if (localVideoTrackRef.current) {
      localVideoTrackRef.current.stop();
      localVideoTrackRef.current.close();
      localVideoTrackRef.current = null;
    }
    if (localAudioTrackRef.current) {
      localAudioTrackRef.current.stop();
      localAudioTrackRef.current.close();
      localAudioTrackRef.current = null;
    }

    if (clientRef.current) {
      clientRef.current.removeAllListeners();
    }
    if (sessionTimerRef.current) {
      clearInterval(sessionTimerRef.current);
    }
    if (autoEndTimerRef.current) {
      clearTimeout(autoEndTimerRef.current);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const getStatusBg = (): string => {
    if (sessionStatus === "active") return "bg-success";
    if (sessionStatus === "error") return "bg-destructive";
    return "bg-primary";
  };

  if (sessionStatus === "error") {
    return (
      <div className="font-sans bg-background min-h-screen text-foreground flex flex-col">
        <div className="bg-card px-3 sm:px-5 py-3 sm:py-4 flex items-center justify-between shadow-sm border-b border-border">
          <div className="flex items-center gap-2 sm:gap-5">
            <span className="text-sm sm:text-lg font-semibold text-primary">
              Session {sessionId}
            </span>
            <span className="bg-destructive text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-semibold">
              Error
            </span>
          </div>
        </div>
        <div className="text-center text-primary text-lg p-5 sm:p-10">
          <div className="bg-destructive/10 border border-destructive/25 text-destructive p-4 rounded-xl mb-5 text-center text-sm sm:text-base">
            {error}
          </div>
          <Button onClick={onBackToClientPortal} radius="md" size="lg" className="mt-5">
            Back to Client Portal
          </Button>
        </div>
      </div>
    );
  }

  if (sessionStatus === "connecting" || sessionStatus === "initializing") {
    return (
      <div className="font-sans bg-background min-h-screen text-foreground flex flex-col">
        <div className="bg-card px-3 sm:px-5 py-3 sm:py-4 flex items-center justify-between shadow-sm border-b border-border">
          <div className="flex items-center gap-2 sm:gap-5">
            <span className="text-sm sm:text-lg font-semibold text-primary">
              Session {sessionId}
            </span>
            <span className="bg-primary text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-semibold">
              Connecting...
            </span>
          </div>
        </div>
        <div className="text-center text-primary text-lg p-5 sm:p-10">
          <Loader color="orange" size="lg" />
          <div className="mt-4">Connecting to video call...</div>
          <div className="text-sm mt-2.5 text-muted-foreground">
            Please allow camera and microphone access when prompted
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans bg-gray-900 min-h-screen text-white flex flex-col">
      {/* Header */}
      <div className="bg-gray-950/80 px-3 sm:px-5 py-2 sm:py-3 flex items-center justify-between gap-2 backdrop-blur-sm">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <span className="text-base sm:text-xl font-bold text-success font-mono whitespace-nowrap">
            {formatTime(sessionTime)}
            {timeRemaining !== null && (
              <span
                className={`text-xs sm:text-sm ml-1 sm:ml-2 ${timeRemaining < 300 ? "text-destructive" : "text-success"}`}
              >
                ({formatTime(timeRemaining)}
                <span className="hidden sm:inline"> remaining</span>)
              </span>
            )}
          </span>
          <span
            className={`${getStatusBg()} text-white px-2 sm:px-3 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap`}
          >
            {sessionStatus}
          </span>
        </div>
        {isTimeUp && (
          <div className="bg-destructive text-white px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold whitespace-nowrap">
            Time's up!
          </div>
        )}
        <Button
          onClick={onBackToClientPortal}
          variant="outline"
          color="red"
          radius="md"
          size="xs"
          className="shrink-0"
        >
          <span className="sm:hidden">Exit</span>
          <span className="hidden sm:inline">Exit Session</span>
        </Button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row p-2 sm:p-4 gap-2 sm:gap-4 relative overflow-hidden">
        {/* Video Area */}
        <div className="flex-1 flex flex-col gap-2 sm:gap-4 min-h-0">
          {/* Desktop: side-by-side grid. Mobile: remote video full + local PIP overlay */}
          <div className="relative flex-1 flex flex-col min-h-0 md:grid md:grid-cols-2 md:gap-4 md:items-stretch">
            {/* Remote Video (full on mobile, half on desktop) */}
            <div className="bg-black rounded-xl overflow-hidden relative flex-1 min-h-0">
              <div className="absolute top-2.5 left-2.5 bg-black/70 text-white px-2.5 py-1 rounded text-sm font-semibold z-10">
                {trainerId ? "Client" : "Trainer"}
              </div>
              <div ref={remoteVideoRef} className="absolute inset-0" />
            </div>

            {/* Local Video: PIP on mobile, grid cell on desktop */}
            <div
              className="bg-black rounded-xl overflow-hidden
                absolute bottom-3 right-3 w-[120px] h-[160px] z-20 border-2 border-white/30 shadow-lg
                md:relative md:inset-auto md:w-auto md:h-auto md:z-auto md:border-0 md:shadow-none"
            >
              <div className="absolute top-1 left-1 md:top-2.5 md:left-2.5 bg-black/70 text-white px-1.5 py-0.5 md:px-2.5 md:py-1 rounded text-[10px] md:text-sm font-semibold z-10">
                You
              </div>
              <div ref={localVideoRef} className="absolute inset-0" />
            </div>
          </div>

          {/* Controls */}
          <div className="flex justify-center items-center gap-3 sm:gap-4 p-3 sm:p-5 bg-gray-950/80 rounded-xl backdrop-blur-sm">
            <button
              onClick={toggleMute}
              className={`w-12 h-12 sm:w-[60px] sm:h-[60px] rounded-full border-none cursor-pointer flex items-center justify-center text-xl sm:text-2xl transition-all text-white ${isMuted ? "bg-destructive" : "bg-gray-700"}`}
              title={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? <IconMicrophoneOff size={24} /> : <IconMicrophone size={24} />}
            </button>

            <button
              onClick={toggleVideo}
              className={`w-12 h-12 sm:w-[60px] sm:h-[60px] rounded-full border-none cursor-pointer flex items-center justify-center text-xl sm:text-2xl transition-all text-white ${isVideoOn ? "bg-gray-700" : "bg-destructive"}`}
              title={isVideoOn ? "Turn off video" : "Turn on video"}
            >
              {isVideoOn ? <IconVideo size={24} /> : <IconVideoOff size={24} />}
            </button>

            <button
              onClick={endSession}
              className="w-16 sm:w-20 h-12 sm:h-[60px] rounded-[30px] border-none cursor-pointer flex items-center justify-center text-xl sm:text-2xl transition-all bg-destructive text-white"
              title="End session"
            >
              <IconPhoneOff size={24} />
            </button>

            {/* Mobile sidebar toggle */}
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="w-12 h-12 sm:w-[60px] sm:h-[60px] rounded-full border-none cursor-pointer flex items-center justify-center text-xl sm:text-2xl transition-all bg-gray-700 text-white md:hidden"
              title="Session info"
            >
              ℹ️
            </button>
          </div>
        </div>

        {/* Sidebar - always visible on desktop, slide-over on mobile */}
        {/* Mobile overlay backdrop */}
        {showSidebar && (
          <div
            className="fixed inset-0 bg-black/50 z-30 md:hidden"
            onClick={() => setShowSidebar(false)}
          />
        )}
        <div
          className={`
          fixed top-0 right-0 h-full w-[85vw] max-w-[320px] z-40 transform transition-transform duration-300
          ${showSidebar ? "translate-x-0" : "translate-x-full"}
          md:static md:translate-x-0 md:w-[280px] md:shrink-0 md:h-auto md:max-w-none md:z-auto
          bg-gray-900/95 md:bg-white/10 rounded-none md:rounded-xl p-4 sm:p-5 backdrop-blur-md md:backdrop-blur-sm border-l border-white/20 md:border overflow-y-auto
        `}
        >
          {/* Mobile close button */}
          <div className="flex items-center justify-between mb-4 md:hidden">
            <h3 className="text-lg font-bold text-primary">Session Info</h3>
            <button
              onClick={() => setShowSidebar(false)}
              className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center text-lg"
            >
              ✕
            </button>
          </div>
          <h3 className="text-xl font-bold mb-5 text-primary hidden md:block">Session Info</h3>

          {/* Pet info and last session — shown to both trainer and client */}
          {petInfo && (
            <>
              <div className="mb-4 sm:mb-5 p-3 sm:p-4 bg-success/10 rounded-lg border border-success/30">
                <h4 className="text-sm sm:text-base mb-2 sm:mb-2.5 text-success">
                  Pet Information
                </h4>
                <div className="text-xs sm:text-sm leading-relaxed">
                  <div>
                    <strong>Name:</strong>{" "}
                    {((petInfo as Record<string, unknown>).pet_name as string) || "N/A"}
                  </div>
                  <div>
                    <strong>Breed:</strong>{" "}
                    {((petInfo as Record<string, unknown>).race as string) || "N/A"}
                  </div>
                  <div>
                    <strong>Age:</strong> {petInfo.age ? `${petInfo.age} years` : "N/A"}
                  </div>
                  {(petInfo as Record<string, unknown>).target_behaviors ? (
                    <div className="mt-2">
                      <strong>Target Behaviors:</strong>{" "}
                      {String((petInfo as Record<string, unknown>).target_behaviors)}
                    </div>
                  ) : null}
                </div>
              </div>

              {lastSessionInfo && (
                <div className="mb-4 sm:mb-5 p-3 sm:p-4 bg-primary/10 rounded-lg border border-primary/30">
                  <h4 className="text-sm sm:text-base mb-2 sm:mb-2.5 text-primary">
                    Last Session Summary
                  </h4>
                  <div className="text-xs sm:text-sm leading-relaxed">
                    {lastSessionInfo.session_summary ? (
                      <div className="mb-2">
                        {lastSessionInfo.session_summary.substring(0, 200)}
                        {lastSessionInfo.session_summary.length > 200 ? "..." : ""}
                      </div>
                    ) : (
                      <div className="text-white/60">No previous session data</div>
                    )}
                    {lastSessionInfo.training_tasks && (
                      <div className="mt-2">
                        <strong>Previous Tasks:</strong>
                        <ul className="mt-1 pl-5">
                          {JSON.parse((lastSessionInfo.training_tasks as string) || "[]")
                            .slice(0, 3)
                            .map((task: string, idx: number) => (
                              <li key={idx}>{task}</li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="mb-4 sm:mb-5">
            <h4 className="text-sm sm:text-base mb-2 sm:mb-2.5 text-primary">
              Participants ({participants.length + 1})
            </h4>
            <div className="p-2 sm:p-2.5 bg-white/10 rounded-lg mb-2 sm:mb-2.5 text-xs sm:text-sm">
              {trainerId ? "You (Trainer)" : "You (Client)"}
            </div>
            {participants.map((participant, index) => (
              <div
                key={participant.uid}
                className="p-2 sm:p-2.5 bg-white/10 rounded-lg mb-2 sm:mb-2.5 text-xs sm:text-sm"
              >
                {trainerId ? "Client" : "Trainer"} {index + 1}
              </div>
            ))}
          </div>

          {trainerId ? (
            <div className="mb-4 sm:mb-5">
              <div className="flex items-center justify-between mb-2 sm:mb-2.5">
                <h4 className="text-sm sm:text-base text-primary">Session Notes</h4>
                <span className="text-[0.7rem] sm:text-xs text-white/60">
                  {notesSaveStatus === "saving" && "Saving..."}
                  {notesSaveStatus === "saved" && "Saved"}
                  {notesSaveStatus === "error" && "Save failed"}
                </span>
              </div>
              <textarea
                className="w-full h-[80px] sm:h-[100px] p-2 sm:p-2.5 rounded-lg border border-white/30 bg-white/10 text-white text-xs sm:text-sm resize-y font-[inherit]"
                placeholder="Add notes about this session..."
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
              />
              <p className="text-[0.7rem] sm:text-xs text-white/60 mt-1.5">
                Notes autosave during the call and pre-fill the post-session form.
              </p>
            </div>
          ) : null}

          {(sessionOptions as Record<string, unknown>).notes ? (
            <div className="mb-4 sm:mb-5">
              <h4 className="text-sm sm:text-base mb-2 sm:mb-2.5 text-primary">Session Goals</h4>
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-2 sm:p-2.5 text-xs sm:text-sm">
                {String((sessionOptions as Record<string, unknown>).notes)}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default VideoCall;
